import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, Landmark, ShieldCheck } from "lucide-react";
import {
  UPI_ID,
  UPI_PAYEE_NAME,
  UPI_URL,
  UPI_APPS,
  BANK_NAME,
  BANK_BRANCH,
  BANK_ACCOUNT_NAME,
  BANK_ACCOUNT_NUMBER,
  BANK_IFSC,
} from "@/lib/contact";

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, delay },
});

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }
};

const CopyButton = ({ value, label, testId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <motion.button
      type="button"
      data-testid={testId}
      onClick={async () => {
        await copyText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      aria-label={`Copy ${label}`}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#6B1D2F]/30 px-3.5 py-1.5 font-mono-brand text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B1D2F] transition-colors duration-300 hover:bg-[#6B1D2F] hover:text-[#FAF3E6]"
    >
      {copied ? <Check size={13} strokeWidth={2.6} /> : <Copy size={13} strokeWidth={2.4} />}
      {copied ? "Copied" : "Copy"}
    </motion.button>
  );
};

const Field = ({ label, value, testId, copyTestId, copyable = true }) => (
  <div>
    <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5C463A]">{label}</p>
    <div className="mt-1.5 flex flex-wrap items-center gap-3">
      <p data-testid={testId} className="font-mono-brand text-base font-semibold text-[#2A1810] sm:text-lg">
        {value}
      </p>
      {copyable && <CopyButton value={value} label={label} testId={copyTestId} />}
    </div>
  </div>
);

const Payment = () => (
  <section data-testid="payment-section" className="bg-[#FAF7F2] px-5 py-16 text-[#2A1810] lg:py-24">
    <div className="mx-auto max-w-6xl">
      <motion.p
        {...reveal()}
        className="mb-4 font-mono-brand text-xs font-semibold uppercase tracking-[0.24em] text-[#A07C3A]"
      >
        Payments
      </motion.p>
      <motion.h2
        {...reveal(0.1)}
        className="font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl"
      >
        Pay by <span className="italic text-[#6B1D2F]">UPI</span> or bank transfer.
      </motion.h2>
      <motion.p {...reveal(0.2)} className="mt-4 max-w-lg text-base leading-relaxed text-[#5C463A] sm:text-lg">
        Scan to pay with any UPI app, or transfer directly to our current account
        for larger orders. Confirm the amount with us before you send.
      </motion.p>

      <motion.div {...reveal(0.3)} className="mt-9 grid gap-5 lg:grid-cols-[auto_1fr]">
        <div className="w-full max-w-[292px] shrink-0 justify-self-center rounded-2xl border border-[#2A1810]/10 bg-white p-6 shadow-[0_10px_30px_rgba(42,24,16,0.06)] lg:justify-self-start">
          <img
            data-testid="payment-upi-qr"
            src={`${process.env.PUBLIC_URL}/upi-qr.svg`}
            alt={`UPI payment QR code for ${UPI_PAYEE_NAME}`}
            width={240}
            height={240}
            className="mx-auto block aspect-square h-auto w-full max-w-[240px]"
          />
          <p className="mt-4 text-center font-mono-brand text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2A1810]">
            {UPI_PAYEE_NAME}
          </p>
        </div>

        <div className="flex flex-col justify-center rounded-2xl border border-[#2A1810]/10 bg-[#F3EBDE] p-6">
          <Field label="UPI ID" value={UPI_ID} testId="payment-upi-id" copyTestId="payment-copy-upi" />
          <p className="mt-6 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5C463A]">
            Pay from your phone
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2.5">
            {UPI_APPS.map((app) => (
              <motion.a
                key={app.name}
                data-testid={`payment-upi-app-${app.name.toLowerCase().replace(/\s+/g, "-")}`}
                href={app.url}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center rounded-full bg-[#6B1D2F] px-5 py-3 text-sm font-semibold text-[#FAF3E6] shadow-[0_10px_24px_rgba(107,29,47,0.22)] transition-colors duration-300 hover:bg-[#521422]"
              >
                {app.name}
              </motion.a>
            ))}
            <motion.a
              data-testid="payment-upi-link"
              href={UPI_URL}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center rounded-full border border-[#6B1D2F]/35 px-5 py-3 text-sm font-semibold text-[#6B1D2F] transition-colors duration-300 hover:bg-[#6B1D2F] hover:text-[#FAF3E6]"
            >
              Other UPI app
            </motion.a>
          </div>
          <p className="mt-2.5 font-mono-brand text-[10px] uppercase tracking-[0.16em] text-[#5C463A]/70">
            Opens on mobile — or scan the code
          </p>
        </div>
      </motion.div>

      <motion.div
        {...reveal(0.36)}
        data-testid="payment-bank-block"
        className="mt-5 rounded-2xl border border-[#2A1810]/10 bg-[#F3EBDE] p-6"
      >
        <div className="flex items-center gap-2.5">
          <Landmark size={18} strokeWidth={2.2} className="text-[#6B1D2F]" />
          <p className="font-display text-lg font-semibold text-[#2A1810]">Bank transfer</p>
        </div>
        <p className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5C463A]/70">
          NEFT · RTGS · IMPS
        </p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <Field label="Account name" value={BANK_ACCOUNT_NAME} testId="payment-bank-name" copyable={false} />
          <Field
            label="Account number"
            value={BANK_ACCOUNT_NUMBER}
            testId="payment-bank-account"
            copyTestId="payment-copy-account"
          />
          <Field label="IFSC" value={BANK_IFSC} testId="payment-bank-ifsc" copyTestId="payment-copy-ifsc" />
          <Field
            label="Bank"
            value={`${BANK_NAME}, ${BANK_BRANCH}`}
            testId="payment-bank-branch"
            copyable={false}
          />
        </div>
      </motion.div>

      <motion.div
        {...reveal(0.44)}
        data-testid="payment-safety-note"
        className="mt-6 flex items-start gap-3 rounded-2xl border border-[#A07C3A]/35 bg-[#F7EFE1] p-5"
      >
        <ShieldCheck size={20} strokeWidth={2.2} className="mt-0.5 shrink-0 text-[#A07C3A]" />
        <p className="text-sm leading-relaxed text-[#5C463A]">
          <span className="font-semibold text-[#2A1810]">
            Before you pay, check that your app shows “{UPI_PAYEE_NAME}”.
          </span>{" "}
          If it shows any other name, stop and call us on the numbers above. Our
          payment details never change — ignore any message claiming otherwise,
          and confirm with us by phone if you are unsure.
        </p>
      </motion.div>
    </div>
  </section>
);

export default Payment;
